import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may see and change tax settings.
 *
 * This mirrors the two bos-api policies exactly, and both carry a constraint
 * that is easy to miss: they require `auth_method=password` **as well as** a
 * role. A BranchAdmin holding a PIN-issued kiosk token is a BranchAdmin, and
 * still receives 403 on every one of these endpoints.
 *
 * | | policy | this client |
 * | --- | --- | --- |
 * | read | `BranchAdmin` = OrgAdmin\|BranchAdmin + password | `readOnly` / `full` |
 * | write | `OrgAdmin` = OrgAdmin + password | `full` |
 *
 * Deriving it here — rather than letting the page discover it from a 403 —
 * is what lets the screen explain *why* it is restricted. A raw 403 tells the
 * user nothing they can act on.
 */
export type SettingsAccess =
    /**
     * A PIN/kiosk session. Every tax-settings endpoint will 403 whatever the
     * role, so the page must not fetch at all — it explains that these settings
     * need a password sign-in.
     */
    | { kind: 'kiosk' }
    /** Signed in, but the role does not permit even reading (e.g. a Seller). */
    | { kind: 'forbidden' }
    /** BranchAdmin: rates and history are readable, saving is not permitted. */
    | { kind: 'readOnly' }
    /** OrgAdmin with a password session: full read and write. */
    | { kind: 'full' };

/**
 * Derive the access level from the persisted session.
 *
 * Fails closed on every unknown: no user, or an `auth_method` that could not be
 * read, is treated as a kiosk session rather than optimistically rendering a
 * page whose every request would 403.
 */
export const deriveSettingsAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): SettingsAccess => {
    if (!user) {
        return { kind: 'kiosk' };
    }
    // Unknown is not password. Rendering the form and letting each request 403
    // is strictly worse than saying why the page is unavailable.
    if (authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    switch (user.role) {
        case 'OrgAdmin':
            return { kind: 'full' };
        case 'BranchAdmin':
            return { kind: 'readOnly' };
        default:
            return { kind: 'forbidden' };
    }
};

/** Whether this access level may read rates and history at all. */
export const canReadSettings = (access: SettingsAccess): boolean =>
    access.kind === 'full' || access.kind === 'readOnly';

/** Whether this access level may record a change. */
export const canWriteSettings = (access: SettingsAccess): boolean =>
    access.kind === 'full';
